import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getResponse } from '../data/chatbotKnowledge';
import chatBoatIcon from "../assets/chatboat-icon.png"

const AIChatBot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    { text: "Hi! I'm your Sahajink AI Assistant. How can I help you today?", isBot: true }
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        setViewportHeight(window.visualViewport.height);
      }
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('scroll', handleResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { text: userMessage, isBot: false }]);
    setInput("");
    setIsTyping(true);

    // Simulate AI thinking
    setTimeout(() => {
      const botResponse = getResponse(userMessage);
      setMessages(prev => [...prev, { text: botResponse, isBot: true }]);
      setIsTyping(false);
    }, 1000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ 
            opacity: 1, 
            y: 0,
            height: window.innerWidth < 640 ? `${viewportHeight}px` : '500px',
            top: window.innerWidth < 640 ? (window.visualViewport?.offsetTop || 0) : 'auto',
            bottom: window.innerWidth < 640 ? 'auto' : 0,
            right: window.innerWidth < 640 ? 0 : 24,
            left: window.innerWidth < 640 ? 0 : 'auto',
          }}
          exit={{ opacity: 0, y: 100 }}
          className={`fixed bg-[#0d1d33] border-white/10 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.5)] z-[1000] flex flex-col overflow-hidden ${
            window.innerWidth < 640 ? 'w-full rounded-0' : 'w-[400px] border rounded-[32px]'
          }`}
        >
          {/* Header */}
          <div className="p-6 bg-blue-600 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-[8px] bg-white/20 flex items-center justify-center text-xl overflow-hidden"><img className='w-10' src={chatBoatIcon} alt='chatBoatIcon' /></div>
              <div>
                <h3 className="text-white font-bold text-sm">Sahajink AI</h3>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-white/70 text-[10px] uppercase tracking-widest font-bold">Online</span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white transition-colors text-2xl">×</button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-lenis-prevent
            className="flex-1 p-6 overflow-y-auto space-y-4 custom-scrollbar"
          >
            {messages.map((msg, i) => (
              <motion.div
                initial={{ opacity: 0, x: msg.isBot ? -10 : 10 }}
                animate={{ opacity: 1, x: 0 }}
                key={i}
                className={`flex ${msg.isBot ? 'justify-start' : 'justify-end'}`}
              >
                <div className={`max-w-[80%] p-4 rounded-2xl text-sm leading-relaxed ${msg.isBot
                  ? 'bg-white/5 text-white border border-white/5 rounded-tl-none'
                  : 'bg-blue-600 text-white rounded-tr-none'
                  }`}>
                  {msg.text.split(/(\[.*?\]\(.*?\))/g).map((part, index) => {
                    const match = part.match(/\[(.*?)\]\((.*?)\)/);
                    if (match) {
                      return <a key={index} href={match[2]} className="text-blue-400 underline hover:text-blue-300">{match[1]}</a>;
                    }
                    return part.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i !== part.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ));
                  })}
                </div>
              </motion.div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white/5 p-4 rounded-2xl rounded-tl-none flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/30 animate-bounce" />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/30 animate-bounce delay-75" />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/30 animate-bounce delay-150" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={handleSend} className="p-6 border-t border-white/5 bg-white/[0.02]">
            <div className="relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything..."
                className="w-full bg-white/5 border border-white/10 rounded-full px-6 py-4 text-white text-base focus:outline-none focus:border-blue-500/50 transition-all pr-14"
              />
              <button
                type="submit"
                className="absolute right-2 top-2 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-500 transition-all shadow-lg"
              >
                →
              </button>
            </div>
          </form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AIChatBot;
